import {
  ProviderSettingsRequestError,
  requestProviderSettings,
} from '@/services/provider/settings-request';
import {
  ExternalPlanSelectionConfig,
  ExternalPlanSelectionMetrics,
  ExternalPlanSelectionState,
  RotateExternalPlanSelectionResult,
  UpdateExternalPlanSelectionInput,
} from '@/types/external-plan-selection';

type ApiPayload = {
  data?: unknown;
  meta?: unknown;
  message?: string;
};

const readBoolean = (value: unknown): boolean | undefined =>
  typeof value === 'boolean' ? value : undefined;

const readNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && !Number.isNaN(value) ? value : undefined;

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' ? value : undefined;

export function normalizeExternalPartnerDomain(domain: string): string | null {
  const trimmed = domain.trim();
  if (!trimmed) {
    return null;
  }

  // Remove protocol prefixes and common subdomain boilerplate.
  let normalized = trimmed.toLowerCase().replace(/^[a-z]+:\/\//, '').replace(/^www\./, '');

  // Strip paths, query params, and fragments.
  const pathIndex = normalized.search(/[/?#]/);
  if (pathIndex >= 0) {
    normalized = normalized.slice(0, pathIndex);
  }

  // Remove ports if present; focus on host entries only.
  if (normalized.includes(':')) {
    normalized = normalized.split(':')[0] ?? '';
  }

  const finalValue = normalized.trim();
  return finalValue.length > 0 ? finalValue : null;
}

function normalizeAllowedDomains(value: unknown, fallback?: string[]): string[] {
  const domains = Array.isArray(value) ? value : fallback ?? [];
  const normalized: string[] = [];

  domains.forEach((item) => {
    if (typeof item !== 'string') {
      return;
    }
    const host = normalizeExternalPartnerDomain(item);
    if (host && !normalized.includes(host)) {
      normalized.push(host);
    }
  });

  return normalized;
}

function normalizeConfig(
  payload: ApiPayload | Record<string, unknown> | undefined,
  fallback?: UpdateExternalPlanSelectionInput,
): ExternalPlanSelectionConfig {
  let record: Record<string, unknown> | undefined;

  if (payload && typeof payload === 'object') {
    const candidate = (payload as ApiPayload).data;
    if (candidate && typeof candidate === 'object') {
      record = candidate as Record<string, unknown>;
    } else {
      record = payload as Record<string, unknown>;
    }
  }

  const allowedDomains = normalizeAllowedDomains(
    record?.allowed_domains ?? record?.allowedDomains,
    fallback?.allowedDomains,
  );

  return {
    partnerName: readString(record?.partner_name ?? record?.partnerName) ??
      fallback?.partnerName ??
      '',
    enabled: readBoolean(record?.enabled) ?? fallback?.enabled ?? false,
    allowedDomains,
    secretPreview: readString(record?.secret_preview ?? record?.secretPreview) ?? null,
    lastRotatedAt: readString(record?.last_rotated_at ?? record?.lastRotatedAt) ?? null,
  };
}

function normalizeMetrics(payload: ApiPayload | undefined): ExternalPlanSelectionMetrics | null {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const metrics =
    (payload.meta as { metrics?: Record<string, unknown> } | undefined)?.metrics ??
    (payload as { metrics?: Record<string, unknown> }).metrics;

  if (!metrics || typeof metrics !== 'object') {
    return null;
  }

  return {
    totalIntents: readNumber(metrics.total_intents ?? metrics.totalIntents) ?? 0,
    fulfilledWithoutPayment:
      readNumber(metrics.fulfilled_without_payment ?? metrics.fulfilledWithoutPayment) ?? 0,
    pendingPayment: readNumber(metrics.pending_payment ?? metrics.pendingPayment) ?? 0,
    completed: readNumber(metrics.completed) ?? 0,
    lastIntentAt: readString(metrics.last_intent_at ?? metrics.lastIntentAt) ?? null,
  };
}

export class ExternalPlanSelectionRequestError extends ProviderSettingsRequestError {
  errors?: Record<string, string[]>;

  constructor(message: string, status?: number, payload?: unknown) {
    super(message, status, payload);
    if (payload && typeof payload === 'object' && 'errors' in (payload as Record<string, unknown>)) {
      this.errors = (payload as { errors?: Record<string, string[]> }).errors;
    }
  }
}

async function requestExternalPlanSelection(
  path: string,
  options: { method?: 'GET' | 'PUT' | 'POST'; body?: Record<string, unknown> | undefined } = {},
): Promise<ApiPayload> {
  try {
    return (await requestProviderSettings(path, options)) as ApiPayload;
  } catch (error) {
    if (error instanceof ProviderSettingsRequestError) {
      throw new ExternalPlanSelectionRequestError(error.message, error.status, error.payload);
    }
    throw new ExternalPlanSelectionRequestError('External plan selection request failed', undefined, error);
  }
}

class ExternalPlanSelectionService {
  async getSettings(): Promise<ExternalPlanSelectionState> {
    const response = await requestExternalPlanSelection('/api/v1/provider/settings/external-plan-selection');
    const config = normalizeConfig(response);
    const metrics = normalizeMetrics(response);

    return {
      config,
      metrics,
    };
  }

  async updateSettings(
    input: UpdateExternalPlanSelectionInput,
  ): Promise<ExternalPlanSelectionConfig> {
    const normalizedDomains = normalizeAllowedDomains(input.allowedDomains);
    const body: Record<string, unknown> = {
      partner_name: input.partnerName,
      enabled: input.enabled,
      allowed_domains: normalizedDomains,
    };

    const response = await requestExternalPlanSelection(
      '/api/v1/provider/settings/external-plan-selection',
      {
        method: 'PUT',
        body,
      },
    );

    return normalizeConfig(response, { ...input, allowedDomains: normalizedDomains });
  }

  async rotateSecret(): Promise<RotateExternalPlanSelectionResult> {
    const response = await requestExternalPlanSelection(
      '/api/v1/provider/settings/external-plan-selection/rotate-secret',
      { method: 'POST' },
    );

    const data = (response.data as Record<string, unknown> | undefined) ?? {};
    const secret = readString(data.secret) ?? '';
    const instructions = readString(data.instructions);

    const configSource =
      (response.meta as { config?: unknown } | undefined)?.config ??
      (response.data as Record<string, unknown> | undefined);

    const config = normalizeConfig(configSource ?? response);

    return {
      secret,
      instructions,
      config,
    };
  }
}

export const externalPlanSelectionService = new ExternalPlanSelectionService();

export {
  normalizeAllowedDomains as normalizeExternalPlanAllowedDomains,
};
